import * as React from 'react'
import { AppService } from '@halo/services/app.service'
import { Container } from 'typescript-ioc/es5'
import { classnames } from '@halo/_utils/classnames'
import { Well, OrgAccessControl, IOrgAccessRoleOption } from '@pushly/aqe/lib/components'
import { OrgAccessRoleIds } from '@pushly/models/lib/enums/rbac'
import { AccountService } from '@halo/services/account.service'
import { CompoundTable } from '@halo/components/compound-table/compound-table'
import { GlobalOutlined, Loading3QuartersOutlined } from '@ant-design/icons'
import { Divider, Flex, Modal, Radio, Segmented, Select, Skeleton, Table } from 'antd'
import { AntdTableEmptyPlaceholder } from '@halo/components/antd-table-empty-placeholder/antd-table-empty-placeholder'
import { UserDto } from '@halo/dtos/user.dto'
import { UserService } from '@halo/services/user.service'
import { AccountDto } from '@halo/dtos/account.dto'
import { DomainDto } from '@halo/dtos/domain.dto'
import { TableRowEntityDisplay } from '@halo/components/table-row-entity-display/table-row-entity-display'
import { IUserPermissionsResponse } from '@halo/interfaces/user-permissions'
import {
    AccessRoleId,
    ExternalAccessRoleIds,
    ExternalApiAccessRoleIds,
    InternalAccessRoleIds,
} from '@halo/enums/access-role.enum'
import AccountUserModel, { ICachedAccountUserRecord } from '@halo/models/access-control/account-user.model'
import DomainUserModel, { ICachedDomainUserRecord } from '@halo/models/access-control/domain-user.model'
import AccessControlService from '@halo/services/access-control.service'
import { AccessRoleMap } from '@halo/models/access-role'
import { observer } from 'mobx-react'
import { AppState } from '@halo/stores/app.state'
import { AbilityAction } from '@halo/enums/ability-action.enum'
import { asCaslSubject } from '@halo/stores/ability.state'
import { SubjectEntity } from '@halo/enums/ability-entity.enum'

const apiRoleIds = [AccessRoleId.INTERNAL_API_USER, ...ExternalApiAccessRoleIds]

/**
 * Derives the User_Access_Well title. When a user name is present, the title is
 * personalized; otherwise it falls back to a generic label so the Add User flow
 * (which has no user yet) never renders "Access for undefined".
 */
export function buildUserAccessWellTitle(name: string | null | undefined): string {
    const trimmed = name?.trim()
    return trimmed ? `Access for ${trimmed}` : 'User Access'
}

interface IUserAccountAccessComboDto extends AccountDto {
    permissions: {
        account?: ICachedAccountUserRecord | Omit<ICachedAccountUserRecord, 'id' | 'role_name'>
        domains: Array<ICachedDomainUserRecord | Omit<ICachedDomainUserRecord, 'id' | 'role_name'>>
    }
}

interface IUserAccountAccessView {
    user: UserDto & any
    userAcs: AccessControlService
    account: IUserAccountAccessComboDto
    onChange?: (acs: AccessControlService) => any
    mode?: 'default' | 'stand-alone' | 'nested'
}

interface IUserAccountAccessViewState {
    domains: DomainDto[]
    loadingDomains: boolean
    selectedAccess: number | undefined
    domainFilter: 'all' | 'production'
}

@observer
export class UserAccountAccessView extends React.Component<IUserAccountAccessView, IUserAccountAccessViewState> {
    protected appState: AppState
    protected appService: AppService
    protected accountService: AccountService
    protected userService: UserService

    public constructor(props: IUserAccountAccessView) {
        super(props)

        this.appState = Container.get(AppState)
        this.appService = Container.get(AppService)
        this.accountService = Container.get(AccountService)
        this.userService = Container.get(UserService)

        this.state = {
            loadingDomains: true,
            domains: [],
            selectedAccess: undefined,
            domainFilter: 'production',
        }
    }

    public componentDidMount(): void {
        this.fetchDomains()
    }

    public render() {
        // Access hasChanges so MobX re-renders when dirty records change
        const _hasChanges = this.props.userAcs.hasChanges

        if (this.props.mode === 'nested') {
            return this.renderNestedMode()
        }
        return this.renderDefaultMode()
    }

    protected renderNestedMode() {
        // Access hasChanges so MobX re-renders this component when bulk apply mutates dirty records
        const _hasChanges = this.props.userAcs.hasChanges

        return (
            <Flex vertical gap={8} className={classnames('user-account-access-nested')}>
                {this.renderDomainToolbar()}
                {this.renderDomainTable()}
            </Flex>
        )
    }

    /**
     * Halo grants organization access to internal users as well, so the role list
     * here is wider than the shared external list. It is passed to the control
     * rather than derived by it: the control has no way to know which surface it
     * is rendering on, and deriving it there would let a change made for one app
     * widen what the other offers.
     */
    protected get orgAccessRoleOptions(): IOrgAccessRoleOption[] {
        let roleIds = this.props.user.isInternalUser ? InternalAccessRoleIds : OrgAccessRoleIds

        if (this.props.user?.id && !this.props.user?.isApiUser) {
            roleIds = roleIds.filter((id) => !apiRoleIds.includes(id))
        }

        return roleIds.map((roleId) => ({
            value: roleId,
            label: AccessRoleMap.get(roleId)?.label ?? '',
        }))
    }

    /**
     * Cascading roles -- internal API users and internal dev users -- take their
     * role from the user record rather than the account_user row, because the row
     * does not govern them.
     */
    protected get currentOrgRoleId(): number {
        if (
            (this.props.user?.isApiUser && this.props.user?.isInternalUser) ||
            this.props.user?.isInternalDevUser
        ) {
            return this.props.user.user_role_id
        }

        return (
            this.props.userAcs.getAccountRecord(this.props.account.id)?.getRoleId() ??
            this.props.userAcs.noAccessRoleId
        )
    }

    protected renderDefaultMode() {
        return (
            <div>
                <OrgAccessControl
                    options={this.orgAccessRoleOptions}
                    value={this.currentOrgRoleId}
                    onChange={this.updateAccountRole}
                    // Only the default layout renders this section -- nested mode routes to
                    // renderNestedMode, which shows the domain table alone.
                    variant="radio"
                    disabled={this.props.user?.isApiUser || this.props.user?.isInternalDevUser}
                    showDisclosure={!this.props.user?.isInternalUser}
                />
                <Divider titlePlacement="start">Domain Access</Divider>
                {this.renderDomainToolbar({ style: { padding: '0 12px 8px' } })}
                <CompoundTable>
                    {this.renderDomainTable()}
                </CompoundTable>
            </div>
        )
    }

    protected renderDomainToolbar(opts?: { style?: React.CSSProperties }) {
        return (
            <Flex justify="space-between" align="center" style={opts?.style}>
                <Segmented
                    className={classnames('domain-filter-toggle')}
                    value={this.state.domainFilter}
                    options={[
                        { label: 'Production Only', value: 'production' },
                        { label: 'All Domains', value: 'all' },
                    ]}
                    onChange={(value) => this.setState({ domainFilter: value as 'all' | 'production' })}
                />
                {this.selectAccessLevel()}
            </Flex>
        )
    }

    protected renderDomainTable() {
        return (
            <Table
                size="small"
                rowKey={(domain: any) =>
                    `${domain.id}-${this.props.userAcs.getDomainRecord(domain.id)?.getRoleId() ?? 'none'}`
                }
                className={classnames('user-domain-access')}
                dataSource={this.getFilteredDomains()}
                loading={
                    this.isLoading === true && {
                        indicator: <Loading3QuartersOutlined spin={true} />,
                    }
                }
                locale={{
                    emptyText: <AntdTableEmptyPlaceholder text="No Domains" icon={<GlobalOutlined />} />,
                }}
                pagination={false}
            >
                <Table.Column key="name" className="name" title="Domain Name" render={this.renderDomain} />
                <Table.Column
                    key="access"
                    className="access"
                    title="Access Level"
                    width={180}
                    render={this.renderAccessPanel}
                />
            </Table>
        )
    }

    protected renderDomain = (_: any, domain: DomainDto) => {
        return <TableRowEntityDisplay title={domain.display_name} status={domain.name} />
    }

    protected selectAccessLevel = () => {
        let availableRoleIds = this.props.user.isInternalUser ? InternalAccessRoleIds : ExternalAccessRoleIds

        if (this.props.user?.id && !this.props.user?.isApiUser) {
            availableRoleIds = availableRoleIds.filter((id) => !apiRoleIds.includes(id))
        }

        const handleChange = this.props.mode === 'nested'
            ? (roleId: number) => this.updateAccountDomainRolesFromSelect(roleId)
            : this.renderConfirmModal

        return (
            <Select
                disabled={this.props.user?.isApiUser || this.props.user?.isInternalDevUser}
                onChange={handleChange}
                style={{ width: 200 }}
                placeholder="Bulk Apply Access Level"
                value={null}
            >
                {availableRoleIds.map((id) => (
                    <Select.Option key={id} value={id}>
                        {AccessRoleMap.get(id)?.label}
                    </Select.Option>
                ))}
            </Select>
        )
    }

    protected renderConfirmModal = (roleId: number) => {
        const roleName = AccessRoleMap.get(roleId)?.label
        const confirm = async () => {
            await this.updateAccountDomainRolesFromSelect(roleId)
        }
        return Modal.confirm({
            okText: 'Yes, Apply',
            cancelText: 'Cancel',
            title: 'Bulk Domain Access Assignment',
            content: (
                <div>
                    <p>
                        Are you sure you want to apply <b>{roleName.toLowerCase()}</b> access to all domains?
                    </p>
                    <p>This action cannot be undone.</p>
                </div>
            ),
            onOk() {
                return confirm()
            },
        })
    }

    protected renderAccessPanel = (_: any, domain: DomainDto) => {
        const modeIsDefault = this.props.mode !== 'nested'

        let defaultRoleId = this.props.userAcs.noAccessRoleId
        const domainPermissions = this.props.userAcs.getDomainRecord(domain.id)

        let roleId = domainPermissions?.getRoleId() ?? defaultRoleId
        if ((this.props.user?.isApiUser && this.props.user?.isInternalUser) || this.props.user?.isInternalDevUser) {
            roleId = this.props.user.user_role_id
        }

        let availableRoleIds = this.props.user.isInternalUser ? InternalAccessRoleIds : ExternalAccessRoleIds

        if (this.props.user?.id && !this.props.user?.isApiUser) {
            availableRoleIds = availableRoleIds.filter((id) => !apiRoleIds.includes(id))
        }

        return modeIsDefault ? (
            <Radio.Group
                value={roleId}
                size="small"
                onChange={(ev) => this.updateAccountDomainRole(domain, ev.target.value)}
                // disable roles assignments for cascading roles
                disabled={this.props.user?.isApiUser || this.props.user?.isInternalDevUser}
            >
                {availableRoleIds.map((id) => (
                    <Radio key={id} value={id}>
                        {AccessRoleMap.get(id)?.label}
                    </Radio>
                ))}
            </Radio.Group>
        ) : (
            <Select
                value={roleId}
                size="small"
                onChange={(value: number) => this.updateAccountDomainRole(domain, value)}
            >
                {availableRoleIds.map((id) => (
                    <Select.Option key={id} value={id}>
                        {AccessRoleMap.get(id)?.label}
                    </Select.Option>
                ))}
            </Select>
        )
    }

    protected get isLoading(): boolean {
        return this.state.loadingDomains
    }

    protected getFilteredDomains(): DomainDto[] {
        let domains = [...this.state.domains]

        if (this.state.domainFilter === 'production') {
            domains = domains.filter((d) => !d.is_demo && !d.is_demo_template && !d.prod_domain_id)
        }

        return domains.sort((a, b) =>
            (a.display_name ?? a.name).localeCompare(b.display_name ?? b.name)
        )
    }

    protected updateAccountDomainRolesFromSelect = async (roleId: number) => {
        const permissionRecords = this.props.userAcs.getCachedUserRecords()
        const noAccessRole = AccessRoleMap.get(this.props.userAcs.noAccessRoleId)
        const accessRole = AccessRoleMap.get(roleId)
        const domainsToApply = this.props.mode === 'nested' ? this.getFilteredDomains() : this.state.domains
        const firstDomain = domainsToApply[0] ? domainsToApply[0] : null
        const domainRecords: DomainUserModel[] = []
        if (accessRole && firstDomain) {
            let orgRecord = permissionRecords.get(firstDomain.account_id)
            if (!orgRecord) {
                orgRecord = new AccountUserModel({
                    account_id: firstDomain.account_id,
                    user_id: this.props.user.id,
                    role_id: noAccessRole!.id,
                    role_name: noAccessRole!.name,
                    flags: [],
                })
            }
            domainsToApply.forEach((domain) => {
                let domainRecord = this.props.userAcs
                    .getCachedUserRecords()
                    .get(domain.account_id)
                    ?.getDomainRecord(domain.id)
                    ?.clone()

                if (domainRecord) {
                    domainRecord.setRole(accessRole)
                } else {
                    domainRecord = new DomainUserModel({
                        account_user_id: orgRecord.getId(),
                        domain_id: domain.id,
                        role_id: accessRole.id,
                        role_name: accessRole.name,
                        flags: [],
                    })
                }
                domainRecords.push(domainRecord)
            })
            await this.props.userAcs.updateCachedRecords(orgRecord.getAccountId(), orgRecord, domainRecords)
            if (this.props.mode !== 'nested') {
                await this.props.userAcs.persistChanges()
            }

            this.props.onChange?.(this.props.userAcs)
        }
    }

    protected async updateAccountDomainRole(domain: DomainDto, roleId: AccessRoleId) {
        const permissionRecords = this.props.userAcs.getCachedUserRecords()
        const noAccessRole = AccessRoleMap.get(this.props.userAcs.noAccessRoleId)
        const accessRole = AccessRoleMap.get(roleId)

        if (accessRole) {
            let orgRecord = permissionRecords.get(domain.account_id)
            if (!orgRecord) {
                orgRecord = new AccountUserModel({
                    account_id: domain.account_id,
                    user_id: this.props.user.id,
                    role_id: noAccessRole!.id,
                    role_name: noAccessRole!.name,
                    flags: [],
                })
            }

            let domainRecord = this.props.userAcs
                .getCachedUserRecords()
                .get(domain.account_id)
                ?.getDomainRecord(domain.id)
                ?.clone()

            if (domainRecord) {
                domainRecord.setRole(accessRole)
            } else {
                domainRecord = new DomainUserModel({
                    account_user_id: orgRecord.getId(),
                    domain_id: domain.id,
                    role_id: accessRole.id,
                    role_name: accessRole.name,
                    flags: [],
                })
            }

            await this.props.userAcs.updateCachedRecord(orgRecord.getAccountId(), orgRecord, domainRecord)
            if (this.props.mode !== 'nested') {
                await this.props.userAcs.persistChanges()
            }

            this.props.onChange?.(this.props.userAcs)
        }
    }

    protected updateAccountRole = async (roleId: number) => {
        const permissionRecords = this.props.userAcs.getCachedUserRecords()
        const accessRole = AccessRoleMap.get(roleId)

        if (accessRole) {
            let orgRecord = permissionRecords.get(this.props.account.id)?.clone()

            if (orgRecord) {
                orgRecord.setRole(accessRole)
            } else {
                orgRecord = new AccountUserModel({
                    account_id: this.props.account.id,
                    user_id: this.props.user.id,
                    role_id: roleId,
                    role_name: accessRole!.name,
                    flags: [],
                })
            }

            await this.props.userAcs.updateCachedRecord(orgRecord.getAccountId(), orgRecord)
            if (this.props.mode !== 'nested') {
                await this.props.userAcs.persistChanges()
            }

            this.props.onChange?.(this.props.userAcs)
        }
    }

    protected async fetchDomains(): Promise<void> {
        if (!this.props.account) {
            setTimeout(() => this.fetchDomains(), 50)
            return
        }

        let update: any = {
            loadingDomains: true,
            domains: [],
        }

        await this.setState(update)

        update = {
            loadingDomains: false,
        }

        const domains = await this.accountService.fetchDomains(this.props.account.id, {
            query: { pagination: false },
            showLoadingScreen: false,
            cancellationKey: `user-view-fetch-account-${this.props.account.id}-domains`,
        })

        if (domains.ok) {
            update.domains = domains.data.filter((domain) => {
                return this.appState.abilityStore.can(
                    AbilityAction.UPDATE,
                    asCaslSubject(SubjectEntity.DOMAIN, {
                        ...domain,
                        statusId: domain.status_id,
                    }),
                )
            })
        }

        await this.setState(update)
    }
}

interface IUserAccessWell {
    user?: UserDto & any
    accountId?: number
    loading?: boolean
    onChange?: (permissions: any) => any
    mode?: 'default' | 'stand-alone' | 'nested'
}

interface IUserAccessWellState {
    propsUserAcs: AccessControlService
    accounts: IUserAccountAccessComboDto[]
    loadingDependencies: boolean
    activeAccount?: IUserAccountAccessComboDto
}

@observer
export class UserAccessWell extends React.Component<IUserAccessWell, IUserAccessWellState> {
    protected appState: AppState
    protected appService: AppService
    protected accountService: AccountService
    protected userService: UserService

    public constructor(props: IUserAccessWell) {
        super(props)

        this.appState = Container.get(AppState)
        this.appService = Container.get(AppService)
        this.accountService = Container.get(AccountService)
        this.userService = Container.get(UserService)

        this.state = {
            propsUserAcs: AccessControlService.forUser(this.user),
            loadingDependencies: false,
            accounts: [],
        }
    }

    public async componentDidMount(): Promise<void> {
        this.fetchDependencies()
    }

    public componentDidUpdate(prevProps: Readonly<IUserAccessWell>) {
        if (this.props.user && this.props.user.user_type_id !== prevProps.user?.user_type_id) {
            this.setState({ propsUserAcs: AccessControlService.forUser(this.props.user) })
        }
    }

    public render() {
        const modeIsDefault = this.props.mode !== 'nested'

        return (
            <Well
                className={classnames('user-access-well', {
                    nested: true,
                    has_changes: this.state.propsUserAcs.hasChanges,
                    ['user-access-nested-well']: !modeIsDefault,
                    ['type-2']: !modeIsDefault,
                    ['type-3']: modeIsDefault,
                })}
                title={this.wellTitle}
                hideFooter={true}
                mode={modeIsDefault ? 'default' : 'ghost'}
                loading={this.props.loading}
                actions={
                    <Select
                        className="account-select"
                        size={modeIsDefault ? 'middle' : 'small'}
                        onChange={this.handleActiveAccountSelection}
                        value={this.isLoading ? undefined : this.props.accountId || this.state.activeAccount?.id}
                        disabled={this.isLoading || !!this.props.accountId}
                        placeholder={this.isLoading ? 'Loading...' : 'Select an Org'}
                        showSearch={true}
                        filterOption={true}
                        optionFilterProp="title"
                    >
                        {!this.isLoading &&
                            this.state.accounts.map((account) => (
                                <Select.Option key={account.id} value={account.id} title={account.name}>
                                    {account.name}
                                </Select.Option>
                            ))}
                    </Select>
                }
            >
                {this.appState.currentUser?.isExternalUser && this.user.isInternalUser ? (
                    <div className="internal-user-message">Internal user access cannot be modified.</div>
                ) : (
                    <Skeleton active={true} loading={this.isLoading} title={false}>
                        {!this.isLoading &&
                            (!this.state.activeAccount ? (
                                <div className="no-account-selected-message">
                                    Please select an organization to view permissions.
                                </div>
                            ) : (
                                <UserAccountAccessView
                                    key={this.state.activeAccount!.id}
                                    mode={this.props.mode}
                                    user={this.user}
                                    userAcs={this.state.propsUserAcs}
                                    account={this.state.activeAccount!}
                                    onChange={(acs) => {
                                        this.props.onChange?.(acs.getDirtyRecords())
                                    }}
                                />
                            ))}
                    </Skeleton>
                )}
            </Well>
        )
    }

    protected get isLoading(): boolean {
        return this.props.loading || this.state.loadingDependencies
    }

    protected get wellTitle(): string {
        return buildUserAccessWellTitle(this.user?.name)
    }

    protected get user(): UserDto {
        return this.props.user ?? new UserDto()
    }

    protected handleActiveAccountSelection = async (accountId?: number): Promise<void> => {
        let activeAccount
        if (accountId) {
            activeAccount = this.state.accounts.find((account) => account.id === accountId)
        }

        return this.setState({ activeAccount })
    }

    protected async fetchDependencies(): Promise<void> {
        const update: Omit<IUserAccessWellState, 'propsUserAcs'> = {
            loadingDependencies: true,
            accounts: [],
        }

        await this.setState({ ...update })
        update.loadingDependencies = false

        const userPermissions = await this.fetchPermissions()
        const reqAccountPermissions = userPermissions.accounts.find((a) => a.id === this.props.accountId)
        const reqAccount = update.accounts.find((a: AccountDto) => a.id === reqAccountPermissions.account_id)

        const { data } = await this.accountService.fetchAll({
            query: {
                pagination: 0,
            },
            showLoadingScreen: false,
            cancellationKey: 'user-view-fetch-accounts',
        })

        update.accounts = data

        if (this.user.isExternalUser && reqAccountPermissions && reqAccount) {
            update.activeAccount = reqAccount
        }

        if (this.props.accountId) {
            update.activeAccount = update.accounts.find((a: AccountDto) => a.id === this.props.accountId)
        }

        const noAccessRoleId = this.user.isInternalUser
            ? AccessRoleId.INTERNAL_NO_ACCESS
            : AccessRoleId.EXTERNAL_NO_ACCESS

        const emptyPermissionsConfig: IUserAccountAccessComboDto['permissions'] = {
            domains: [],
        }

        for (const acct of update.accounts) {
            acct.permissions = acct.permissions ?? { ...emptyPermissionsConfig }

            const acctPermissions: ICachedAccountUserRecord = userPermissions.accounts.find(
                (perms) => perms.account_id === acct.id,
            )

            let domainPermissions: ICachedDomainUserRecord[] = []
            if (acctPermissions?.id) {
                domainPermissions = userPermissions.domains.filter((d) => (d.account_user_id = acctPermissions.id))
            }

            acct.permissions.account = acctPermissions ?? {
                account_id: acct.id,
                user_id: this.user.id,
                role_id: noAccessRoleId,
                flags: [],
            }
            acct.permissions.domains = domainPermissions
        }

        await this.setState(update)
    }

    protected async fetchPermissions(): Promise<IUserPermissionsResponse> {
        const permissions: IUserPermissionsResponse = {
            accounts: [],
            domains: [],
        }

        if (this.user.id) {
            await this.state.propsUserAcs.getUserRecords()

            const opts: any = {
                showLoadingScreen: false,
                cancellationKey: 'user-view-fetch-perms',
            }

            const {
                data: { accounts, domains },
            } = await this.userService.fetchPermissions(this.user.id, opts)

            permissions.accounts = accounts
            permissions.domains = domains
        }

        return permissions
    }
}
